// Conversation detail — the note_doc viewer (conversation-note redesign PR2, spec §7).
// README flow + sticky outline (note-layout mockup B) on the overhaul design system: header with a
// single plain meta line, overview (lead → why → highlights → where-it-stands → jump links), the
// sticky outline rail, typed sections (NoteSections), and a footer with entity tags + a raw-note
// toggle. Renders deriveNoteDoc(data) — never re-parses compacted_text for a row with a stored doc.

import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { siteLabel } from '../../lib/sites';
import { formatSourceDate } from '../../lib/formatting';
import { deriveNoteDoc, sectionLabel, type NoteSection } from '../../lib/note-doc';
import { NoteSections, outlineEntries } from '../note/NoteSections';
import { NoteOutline } from '../note/NoteOutline';
import { RichText } from '../brief/richText';
import type { ConversationDetailData } from '../../hooks/useConversationDetail';
import type { TeamConversationDetailData } from '../../hooks/useTeamConversationDetail';
import type { DetailBack } from '../../hooks/useDetailBack';

interface ConversationDetailSurfaceProps {
  data: ConversationDetailData | TeamConversationDetailData;
  onNav: (id: string) => void;
  mode?: 'individual' | 'team';
  // History-aware back (wired by the route wrappers). It carries its own LABEL — this button is
  // fed by ~30 call sites across the brief, Ask, Actions, standup and Settings, and a hardcoded
  // "Map" was wrong for nearly all of them.
  back?: DetailBack;
}

function isTeamData(data: ConversationDetailData | TeamConversationDetailData): data is TeamConversationDetailData {
  return 'author_name' in data;
}

const STATE_BADGE: Record<string, { label: string; cls: string }> = {
  shipped: { label: 'Shipped', cls: 'grn' },
  still_building: { label: 'Still building', cls: 'amb' },
  discussed: { label: 'Discussed', cls: 'dim' },
};

// Jump-link count: bullets are the unit users skim for; a figure/code block counts as one item.
function sectionCount(section: NoteSection): number {
  return (section.blocks ?? []).reduce((n, b) => {
    const bullets = Array.isArray(b?.bullets) ? b.bullets.length : 0;
    const figures = (b?.code ? 1 : 0) + (b?.table ? 1 : 0) + (b?.chart ? 1 : 0);
    return n + bullets + figures;
  }, 0);
}

// Track which outline anchor is in view. jsdom (unit tests) has no IntersectionObserver — the
// outline then simply never highlights, which is fine for static render assertions.
function useActiveSection(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || ids.length === 0) return;
    const visible = new Set<string>();
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) visible.add(e.target.id);
        else visible.delete(e.target.id);
      }
      const first = ids.find(id => visible.has(id));
      if (first) setActive(first);
    }, { rootMargin: '-10% 0px -70% 0px' });
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
  }, [ids]);
  return active;
}

function jumpTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function ConversationDetailSurface({ data, onNav, mode = 'individual', back }: ConversationDetailSurfaceProps) {
  const isTeam = mode === 'team';
  const doc = useMemo(() => deriveNoteDoc(data), [data]);
  const entries = useMemo(() => outlineEntries(doc.sections), [doc]);
  const entryIds = useMemo(() => entries.map(e => e.id), [entries]);
  const activeId = useActiveSection(entryIds);
  const [showRaw, setShowRaw] = useState(false);

  const isPrivate = !isTeam && 'private' in data && (data as ConversationDetailData).private;
  const speakerStats = data.speaker_stats ?? null;
  const projectName = (isTeam && 'team_project_name' in data && data.team_project_name)
    ? data.team_project_name
    : (data.project_name ?? null);
  const teamProjectId = isTeam && 'team_project_id' in data ? (data.team_project_id ?? null) : null;
  const badge = data.briefing_category ? STATE_BADGE[data.briefing_category] : undefined;
  // UTC-pinned label (capture timestamps are ISO UTC; a machine-local render would shift the date across
  // midnight boundaries and make unit tests timezone-dependent), now via the SHARED formatter this file's
  // inline copy was extracted into. Two reasons it is shared rather than duplicated: an Ask citation
  // deep-links straight here, so the source card and this page must never date the same note differently;
  // and the shared version appends the year outside the current one — a bare "Feb 6" on a 2023 note reads
  // as this February, and 8.8% of the corpus predates this year. Returns null on an unparseable value, so
  // the push is conditional — an unconditional one would put a dangling "·" in the meta line.
  const shortDate = formatSourceDate(data.last_captured_at);

  const navProject = () => {
    if (!projectName) return;
    if (teamProjectId) onNav(`map?view=list&group=project&focus=${teamProjectId}`);
    else onNav(`map?view=list&search=${encodeURIComponent(projectName)}`);
  };

  // One plain meta line (overview-v3 mockup): site · date · author · project · state (+ granola
  // turn dynamics, + PRIVATE), interleaved with · separators.
  const segs: ReactNode[] = [];
  segs.push(siteLabel(data.site));
  if (shortDate) segs.push(shortDate);
  if (isTeam && isTeamData(data)) segs.push(data.author_name);
  if (projectName) segs.push(
    <button type="button" className="nd-proj" onClick={navProject}>{projectName}</button>
  );
  if (badge) segs.push(<span className={`nd-state ${badge.cls}`}>{badge.label}</span>);
  if (speakerStats?.n_speakers != null) {
    segs.push(`${speakerStats.n_speakers} ${speakerStats.n_speakers === 1 ? 'speaker' : 'speakers'}`);
  }
  if (speakerStats?.owner_share != null) {
    segs.push(`owner spoke ~${Math.round(speakerStats.owner_share * 100)}%`);
  }
  if (speakerStats?.duration_seconds != null && speakerStats.duration_seconds >= 60) {
    segs.push(`~${Math.round(speakerStats.duration_seconds / 60)} min`);
  }
  // PR-3 "Open in source": the artifact this note distilled, when the BFF could derive a link the
  // viewer is entitled to (null for ai-chat/claude-code, a teammate's email, or a missed mirror
  // join — this page is the designed fallback). Truthiness, not != null: an empty string would
  // render a dead link. Last content segment so the line still reads site-first as provenance.
  if (data.source_url) segs.push(
    <a className="nd-src" href={data.source_url} target="_blank" rel="noopener noreferrer">Open in source ↗</a>
  );
  if (isPrivate) segs.push(<span className="nd-private">PRIVATE</span>);

  return (
    <div className="v2-content nd-page">
      <button type="button" className="nd-back" onClick={() => (back ? back.go() : onNav('map?view=list'))}>
        ‹ {back ? back.label : 'Map'}
      </button>

      <h1 className="nd-title">{data.conversation_title || data.topic || 'Untitled conversation'}</h1>
      <div className="nd-meta">
        {segs.map((s, i) => (
          <Fragment key={i}>
            {i > 0 && <span className="sep">·</span>}
            {s}
          </Fragment>
        ))}
      </div>

      <div className="nd-overview">
        {data.summary && <p className="nd-lead">{data.summary}</p>}
        {doc.overview.why_it_matters && (
          <>
            <div className="nd-h">Why this happened</div>
            <p className="nd-p">{doc.overview.why_it_matters}</p>
          </>
        )}
        {doc.overview.highlights.length > 0 && (
          <>
            <div className="nd-h">Highlights</div>
            <ul className="nd-ul">
              {doc.overview.highlights.map((h, i) => (
                <li key={i}><RichText text={h} onNav={onNav} codeSpans /></li>
              ))}
            </ul>
          </>
        )}
        {doc.overview.where_it_stands && (
          <>
            <div className="nd-h">Where it stands</div>
            <div className="nd-stand"><p className="nd-p">{doc.overview.where_it_stands}</p></div>
          </>
        )}
        {doc.sections.length > 0 && (
          <div className="nd-jumps">
            {doc.sections.map((s, i) => (
              <Fragment key={s.kind}>
                {i > 0 && <span className="sep">·</span>}
                <button type="button" onClick={() => jumpTo(`nd-sec-${s.kind}`)}>
                  {sectionLabel(s.kind)} <span className="n">{sectionCount(s)}</span>
                </button>
              </Fragment>
            ))}
          </div>
        )}
      </div>

      <div className="nd-body">
        <NoteOutline entries={entries} activeId={activeId} onJump={jumpTo} />
        <NoteSections sections={doc.sections} onNav={onNav} />
      </div>

      <div className="nd-footer">
        {data.entity_tags.length > 0 && (
          <div className="nd-tags">
            {data.entity_tags.map(tag => (
              <button key={tag} type="button" className="nd-tag"
                onClick={() => onNav(`map?view=list&search=${encodeURIComponent(tag)}`)}>
                {tag}
              </button>
            ))}
          </div>
        )}
        <button type="button" className="nd-rawtoggle" onClick={() => setShowRaw(s => !s)}>
          {showRaw ? 'Hide raw note' : 'View raw note'}
        </button>
        {showRaw && <pre className="nd-raw">{data.compacted_text}</pre>}
      </div>
    </div>
  );
}
